<script setup lang="ts">
import { computed, unref } from 'vue';
import { MULTI_DOC_MAX_FILES_PER_APPOINTMENT, t } from '@nova/core';
import { useCustomFieldProps } from '@satellite/composables/useCustomFieldProps';
import { useCustomFieldValidation } from '@satellite/composables/useCustomFieldValidation';
import { useCustomFormsStorageContext } from '@satellite/composables/useCustomFormsStorageContext';

import MultidocumentUploadButton from '../MultidocumentUploadButton.vue';
import type { CustomFieldProps } from '../types';
import CustomFieldInputBase from '../CustomFieldInputBase.vue';

/**
 * Multi-document field type - multiple file uploads.
 * Requires storage context via CUSTOM_FORMS_STORAGE_CONTEXT_KEY (warehouseId, storageEndpoint).
 */
const {
  hideDetails = 'auto',
  displayLabel = true,
  required = undefined,
  ...props
} = defineProps<CustomFieldProps>();

const emit = defineEmits<{
  'update:model-value': [value: string[]];
  /** @deprecated Use update:model-value instead */
  input: [value: string[]];
  'toggle-confirm-button': [uploading: boolean];
}>();

const { normalizedProps } = useCustomFieldProps(() => ({
  ...props,
  hideDetails,
  displayLabel,
  required
}));
const storageContext = useCustomFormsStorageContext();
const { rules: validationRules } = useCustomFieldValidation(
  computed(() => ({ ...props.field, required: normalizedProps.required }))
);

const modelValueArray = computed(() => (Array.isArray(props.modelValue) ? props.modelValue : []));

const uploadCaption = computed(() =>
  t('customForms.messages.maximumInSizePerFileOnlyImageTextZipSpreadsheetAllowed', {
    size: '50MB',
    count: MULTI_DOC_MAX_FILES_PER_APPOINTMENT,
    image: t('global.basicTerms.image'),
    text: t('global.basicTerms.text'),
    zip: t('global.basicTerms.zip'),
    spreadsheet: t('global.basicTerms.spreadsheet')
  })
);

function handleUpdate(value: string[]) {
  emit('update:model-value', value);
  emit('input', value);
}
</script>

<template>
  <custom-field-input-base :field="field" v-bind="normalizedProps">
    <div class="custom-field-multidocument">
      <multidocument-upload-button
        :warehouse-id="unref(storageContext).warehouseId"
        :storage-endpoint="unref(storageContext).storageEndpoint"
        :model-value="unref(modelValueArray)"
        :required="normalizedProps.required"
        :rules="validationRules"
        :read-only="normalizedProps.readOnly"
        :label="field.label ?? 'Upload documents'"
        :placeholder="field.placeholder"
        :variant="normalizedProps.outlined ? 'simple' : 'default'"
        @update:model-value="handleUpdate"
        @uploading="emit('toggle-confirm-button', true)"
        @uploaded="emit('toggle-confirm-button', false)"
        @error="emit('toggle-confirm-button', false)" />
      <span class="typography-caption">{{ uploadCaption }}</span>
    </div>
  </custom-field-input-base>
</template>

<style scoped lang="scss">
.custom-field-multidocument {
  width: 100%;
}
</style>
